import icn_line_chart from "assets/images/icons/icn_line_chart.png";
import icn_info_solid from "assets/images/icons/icn_Info_solid.svg";

import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Tooltip,
  tooltipClasses,
  CircularProgress,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import RogueAPDetectionChart from "../RogueAPDetectionChart";
import { SolidButton } from "components/buttons/SolidButton";
import { ReactComponent as Clock } from "assets/images/icons/icn_display_clock.svg";

const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: "#4D4D4D",
    color: theme.palette.primary.white,
    maxWidth: 600,
    fontSize: theme.typography.pxToRem(12),
    padding: "16px 20px",
    borderRadius: "8px",
    lineHeight: "1.5",
    boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.3)",
  },
}));

const TooltipContent = () => (
  <Box>
    <Typography variant="body2" sx={{ fontSize: "12px" }}>
      This dashboard provides real-time wireless security visibility by
      detecting suspicious BSSIDs broadcasting the organization's SSIDs.
      Detection is performed at the BSSID level, while SSID indicators are
      aggregated and presented at the site level to highlight potential rogue
      access points or unauthorized network impersonation.
    </Typography>
  </Box>
);

const RogueAPDetection = ({
  onClick,
  noData = false,
  rogueData = [],
  radioData,
  wirelessChannelInterfaceApiData,
  chartRogueData = [],
  loading = false,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const summaryData = Array.isArray(rogueData) ? rogueData : [];
  const lineChartData = Array.isArray(chartRogueData) ? chartRogueData : [];

  const hasSummaryData = summaryData.length > 0;
  const hasChartData = lineChartData.length > 0;
  const hasData = hasSummaryData || hasChartData;
  const isNeighborApDisabled = !radioData?.["wireless.radioGlobal"]?.neighborApDetection;

  const getFormatLastScanTime = (value) => {
    if (!value) return "-";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "-";

    return new Intl.DateTimeFormat("zh-TW", {
      timeZone: "Asia/Taipei",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    }).format(date);
  };

  const formatLastScanTime = getFormatLastScanTime(
    wirelessChannelInterfaceApiData?.latestTs,
  );

  return (
    <Box
      onClick={noData || isNeighborApDisabled ? undefined : onClick}
      sx={{
        border: `1px solid ${theme.palette.primary.greyThread}`,
        borderRadius: theme.shape.customBorderRadius[8],
        p: 2.5,
        bgcolor: theme.palette.primary.white,
        mb: 3,
        cursor: onClick && !noData && !isNeighborApDisabled ? "pointer" : "default",
        "&:hover":
          onClick && !noData && !isNeighborApDisabled
            ? {
              boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.05)",
            }
            : {},
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Box
            component="img"
            src={icn_line_chart}
            sx={{ width: "18px", height: "18px", mr: 1 }}
          />

          <Typography
            sx={{
              fontWeight: theme.typography.customFontWeight[1],
              color: theme.palette.primary.main,
              fontSize: theme.typography.customFontSize[3],
              mr: 1,
            }}
          >
            Rogue AP Detection
          </Typography>

          <HtmlTooltip title={<TooltipContent />} placement="auto">
            <Box
              component="img"
              src={icn_info_solid}
              sx={{ width: "16px", height: "16px", cursor: "pointer" }}
            />
          </HtmlTooltip>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            color: theme.palette.primary.main,
          }}
        >
          <Clock />
          <Typography
            sx={{
              fontSize: theme.typography.customFontSize[2],
              fontWeight: theme.typography.customFontWeight[2],
            }}
          >
            Last Scan : {formatLastScanTime}
          </Typography>
        </Box>
      </Box>

      {loading ? (
        <Box
          sx={{
            minHeight: "320px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      ) : isNeighborApDisabled ? (
        <Box
          sx={{
            minHeight: "380px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <Typography
              sx={{
                fontWeight: theme.typography.customFontWeight[1],
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[3],
                mb: 1
              }}
            >
              Neighbor AP Detection Required
            </Typography>
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
                mb: 2
              }}
            >
              Neighbor AP Detection is currently disabled. Turn on this feature to monitor surrounding access points.
            </Typography>

            <SolidButton
              width="155px"
              height="32px"
              onClick={() => navigate("/site/settings", { state: { navigateTab: "wireless-radio" } })}
            >
              Go to Settings
            </SolidButton>
          </Box>
        </Box>
      ) : noData || !hasData ? (
        <Box
          sx={{
            minHeight: "380px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{
                color: theme.palette.primary.greyThird,
                fontSize: theme.typography.customFontSize[2],
              }}
            >
              No data available
            </Typography>
          </Box>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              alignItems: { xs: "stretch", md: "flex-end" },
              px: 2,
              pt: 1,
              pb: 2,
              mb: 2,
              borderBottom: `1px solid ${theme.palette.primary.greyThread}`,
              gap: { xs: 2, md: 0 },
            }}
          >
            {summaryData.map((item, index) => (
              <Box
                key={`${item.label}-${index}`}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  pl: { xs: 0, md: index === 0 ? 0 : 2 },
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "flex-end",
                }}
              >
                <Box
                  sx={{
                    mb: 0.5,
                    fontSize: theme.typography.customFontSize[2],
                    fontWeight: theme.typography.customFontWeight[1],
                    color: theme.palette.primary.greyPrimary,
                  }}
                >
                  {item.label}
                </Box>

                <Box
                  sx={{
                    fontSize: theme.typography.customFontSize[6],
                    fontWeight: theme.typography.customFontWeight[1],
                    color: theme.palette.primary.main,
                  }}
                >
                  {item.value ?? 0}
                </Box>
              </Box>
            ))}
          </Box>

          <Box sx={{ px: 1, pb: 1, height: "260px" }}>
            <RogueAPDetectionChart data={lineChartData} />
          </Box>
        </>
      )}
    </Box>
  );
};

export default RogueAPDetection;
